import React, { useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import moment from 'moment';
import { FiClock, FiTruck } from 'react-icons/fi';
import { useDispatch } from 'react-redux';

import { FetchRestockSkuPoBreakdown } from '../../slices/restock';

/*
 * PO breakdown modal for the "Pending on PO" and "WH Inbound" cells — shared across the Restock, SKU,
 * Products, Database, and Catalog pages. Shows the purchase-order lines behind the number (Created Date
 * / PO Name / Qty / Unit Cost); the PO name links to its detail page in a new tab.
 *   `params` — what to fetch by: { sku_id } (SKU/restock rows) or { upc } (product/catalog/db rows).
 *   `label`  — subtitle shown under the title (the SKU string or UPC).
 *   `type`   — 'pending' | 'inbound' selects which list to display.
 */
export default function PoBreakdownModal({ open, onClose, params, label, type }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ pending: [], inbound: [] });

  // Stable primitive key so a fresh `params` object literal each render doesn't retrigger the fetch.
  const paramKey = params ? (params.sku_id != null ? `s:${params.sku_id}` : `u:${params.upc}`) : null;

  useEffect(() => {
    if (!open || !paramKey || !params) return undefined;
    let alive = true;
    setLoading(true);
    dispatch(FetchRestockSkuPoBreakdown(params)).unwrap()
      .then((res) => { if (alive) setData(res?.data || { pending: [], inbound: [] }); })
      .catch(() => { if (alive) setData({ pending: [], inbound: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paramKey, dispatch]);

  const isInbound = type === 'inbound';
  const rows = (isInbound ? data.inbound : data.pending) || [];

  const th = { textAlign: 'left', fontSize: 11.5, fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '9px 24px', background: '#F9FAFB', borderBottom: '1px solid #EAECF0', position: 'sticky', top: 0 };
  const thR = { ...th, textAlign: 'right' };
  const td = { fontSize: 13, color: '#101828', padding: '8px 24px', borderBottom: '1px solid #F2F4F7' };
  const tdR = { ...td, textAlign: 'right' };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      width={720}
      centered
      title={null}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
      zIndex={100000}
      className="restock-breakdown-modal"
      maskStyle={{ background: 'rgba(16, 24, 40, 0.45)' }}
    >
      {/* Header */}
      <div style={{ padding: '26px 28px 24px', background: 'linear-gradient(180deg, #FCFCFD 0%, #FFFFFF 100%)', borderBottom: '1px solid #F2F4F7' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isInbound ? '#ECFDF3' : '#EFF8FF', color: isInbound ? '#039855' : '#1570EF', flexShrink: 0 }}>
            {isInbound ? <FiTruck size={20} /> : <FiClock size={20} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1B2559' }}>{isInbound ? 'WH Inbound' : 'Pending on PO'} breakdown</div>
            <div style={{ fontSize: 12.5, color: '#98A2B3', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 560 }}>
              {label || ''}
            </div>
          </div>
        </div>
    
      </div>

      {/* Table */}
      <Spin spinning={loading}>
        <div style={{ maxHeight: 360, overflow: 'auto', borderTop: '1px solid #EAECF0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Created Date</th>
                <th style={th}>PO Name</th>
                <th style={thR}>Qty</th>
                <th style={thR}>Unit Cost</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#98A2B3', padding: '30px 16px' }}>
                  No {isInbound ? 'incoming' : 'pending'} purchase orders for this SKU.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.po_id}-${r.upc}-${i}`} className="restock-breakdown-row">
                  <td style={{ ...td, color: '#667085', whiteSpace: 'nowrap' }}>{r.created_at ? moment(r.created_at).format('DD MMM YYYY') : 'N/A'}</td>
                  <td style={td}>
                    {/* Open the PO detail page in a new tab. */}
                    <a
                      href={`/po-details/${r.po_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 600, color: '#1565D8', textDecoration: 'none' }}
                    >
                      {r.po_name || 'N/A'}
                    </a>
                    {r.upc ? <span style={{ color: '#98A2B3', fontSize: 11.5, marginLeft: 6 }}>· {r.upc}</span> : null}
                  </td>
                  <td style={{ ...tdR, fontWeight: 600 }}>{Number(r.qty).toLocaleString()}</td>
                  <td style={tdR}>${Number(r.unit_cost || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Spin>
    </Modal>
  );
}
